/**
 * URL contract for saved-view display options.
 *
 * The saved view's arrangement — layout and any params the fetch respects — is expressed
 * as plain named URL parameters, never an encoded blob. Layout consumers read this
 * contract; the options menu writes it via `useSavedViewOptions`.
 *
 * `groupBy` is readable but not yet writable — the board mapper hardcodes label grouping.
 * Sort/swimlane will join here as separate params when the fetch honours them.
 */

export const LAYOUT_PARAM = 'layout'
export const GROUP_BY_PARAM = 'group_by'

// TODO: rm once a real group-by selector writes `GROUP_BY_PARAM` itself. Until then, Planning
// requests and caches as though Label were always chosen, matching what the options menu
// displays (`SavedViewOptionsMenu.tsx`).
export const DEFAULT_GROUP_BY = 'label'

export const LAYOUTS = ['list', 'board'] as const
export type Layout = (typeof LAYOUTS)[number]

export interface SavedViewUrlParams {
  layout: Layout
  groupBy: string | null
}

function isLayout(value: string | null): value is Layout {
  return value !== null && (LAYOUTS as readonly string[]).includes(value)
}

function readParam(search: URLSearchParams, key: string): string | null {
  return search.get(key)?.trim() || null
}

export function readSavedViewUrlParams(search: URLSearchParams): SavedViewUrlParams {
  const rawLayout = search.get(LAYOUT_PARAM)
  return {
    layout: isLayout(rawLayout) ? rawLayout : 'list',
    groupBy: readParam(search, GROUP_BY_PARAM),
  }
}
